/**
 * checkrApi.js
 *
 * Talks to the createCheckrScreening Lambda to start (or resume) a Checkr
 * background screening for a rental application. The application form
 * collects DOB (required) and SSN (optional) directly so most applicants
 * never need a separate Checkr hand-off — anything left blank is instead
 * collected by Checkr's own hosted apply flow.
 *
 * Auth: goes through apiFetch, which sends the HttpOnly firebaseToken
 * cookie set by the /login Lambda and transparently refreshes it (or signs
 * the user out) if the cookie has gone stale.
 */
import { apiFetch } from "./apiFetch";

const API_BASE_URL =
  import.meta.env.VITE_APP_API_URL || "https://auth.ezron.ai";

/**
 * Creates (or reuses) the Checkr candidate + hosted invitation for a rental
 * application. Safe to call more than once — the Lambda reuses existing
 * Checkr resources instead of creating duplicates.
 * @param {string} applicationId
 * @param {{ dob?: string, ssn?: string }} [pii] Optional DOB/SSN collected on
 *  the application form. Sent directly to the Lambda for this one request
 *  only — never persisted client-side or written to Firestore.
 * @param {string} [applicantId] When set, screens a co-applicant (an
 *  additional adult occupant's own applicants/{applicantId} sub-doc)
 *  instead of the primary applicant on the household application.
 * @returns {Promise<{ candidateId: string, invitationId: string, invitationUrl: string|null, status: string }>}
 */
export async function startBackgroundScreening(applicationId, pii = {}, applicantId = "") {
  const res = await apiFetch(`${API_BASE_URL}/createCheckrScreening`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ applicationId, applicantId, dob: pii.dob || "", ssn: pii.ssn || "" }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `createCheckrScreening returned ${res.status}`);
  }
  return res.json();
}

/**
 * Fetches the live Checkr report for an application's background check
 * (GET /orders/{id}/report under the hood). Returns null fields for any
 * product still pending — throws a distinct "not ready" error if Checkr
 * hasn't produced a report at all yet (order still waiting on the applicant).
 * @param {string} applicationId
 * @param {string} [applicantId] When set, fetches a co-applicant's own report instead of the primary's.
 * @returns {Promise<object>} The raw Checkr report.
 */
export async function getScreeningReport(applicationId, applicantId = "") {
  const params = new URLSearchParams({ applicationId });
  if (applicantId) params.set("applicantId", applicantId);
  const res = await apiFetch(`${API_BASE_URL}/getScreeningReport?${params.toString()}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `getScreeningReport returned ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

/**
 * Downloads the Checkr-generated PDF for a completed screening report and
 * triggers a browser save-as. The Lambda proxies Checkr's synchronous
 * GET /reports/{id}/pdf as base64 JSON so this doesn't depend on API
 * Gateway binary-media-type configuration.
 * @param {string} applicationId
 * @param {string} [applicantId] When set, downloads a co-applicant's own report instead of the primary's.
 */
export async function downloadScreeningReportPdf(applicationId, applicantId = "") {
  const params = new URLSearchParams({ applicationId });
  if (applicantId) params.set("applicantId", applicantId);
  const res = await apiFetch(`${API_BASE_URL}/getScreeningReportPdf?${params.toString()}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getScreeningReportPdf returned ${res.status}`);
  }
  const { pdfBase64, filename } = await res.json();
  const byteChars = atob(pdfBase64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "screening-report.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Called ONCE right after the client's own Stripe confirmPayment() succeeds.
 * Verifies the payment directly against Stripe, promotes the pending draft,
 * seeds every co-applicant's sub-doc, and creates every household member's
 * Checkr order — all in one request, using the DOB/SSN supplied here (never
 * written to Firestore for anyone). Replaces the old client-side polling
 * dance that raced against the async Stripe webhook.
 * @param {string} pendingApplicationId
 * @param {string} paymentIntentId
 * @param {{ dob?: string, ssn?: string }} primary
 * @param {Array<{ email: string, dob?: string, ssn?: string }>} coApplicants
 * @returns {Promise<{ applicationId: string, primary: object, coApplicants: object[] }>}
 */
export async function finalizeRentalApplication(pendingApplicationId, paymentIntentId, primary = {}, coApplicants = []) {
  const res = await apiFetch(`${API_BASE_URL}/finalizeRentalApplication`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      pendingApplicationId,
      paymentIntentId,
      dob: primary.dob || "",
      ssn: primary.ssn || "",
      coApplicants: coApplicants.map((p) => ({ email: p.email, dob: p.dob || "", ssn: p.ssn || "" })),
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `finalizeRentalApplication returned ${res.status}`);
  }
  return res.json();
}

